import React, {useState, useRef} from 'react';
import {Glyphs, ControlButton, TscButtonType, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {SearchBarInput, SearchBarContainer, SearchForm} from '../util/StyledElements';
import styled from 'styled-components';
import {ButtonActivationKeys} from '../../constants/Constants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';

const ClearButtonWrapper = styled.div<{isVisible: boolean; inputHasFocus: boolean}>`
   display: ${props => props.inputHasFocus ? 'block' : 'none'};
   visibility: ${props => props.isVisible ? 'visible' : 'hidden'};

   svg {
      height: 0.875rem;
      width: 0.875rem;
   }
`;

export const CommentFilterWrapper = styled.div`
   * {
      margin: 0 !important;
   }
`;

export const CommentFilterBase: React.FC<ICommentFilterProps & IStateMappedProps> = ({searchText, setSearchText, executeSearch, theme}) => {
   const {t} = useTranslation();
   const inputRef = useRef<HTMLInputElement>(null);
   const [hasFocus, setHasFocus] = useState(false);

   const onSearchButtonClicked = (event: React.MouseEvent) => {
      event.preventDefault();

      if (searchText.length) {
         executeSearch();
      }

      if (inputRef.current) {
         inputRef.current.focus();
      }
   };

   const onKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === ButtonActivationKeys.enter && !e.shiftKey) {
         e.stopPropagation();
         e.preventDefault();

         if (searchText.trim().length) {
            executeSearch();
         }
      }
   };

   const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      return false;
   };

   return (
      <CommentFilterWrapper>
         <SearchBarContainer theme={theme} hasFocus={hasFocus} onBlur={() => !searchText ? setHasFocus(false) : null} onClick={() => setHasFocus(true)}>
            <SearchForm onSubmit={handleOnSubmit}>
               <SearchBarInput theme={theme} hasFocus={hasFocus} maxLength={80} aria-owns="search-results" aria-label={t('search.search')} value={searchText} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value)} ref={inputRef} className="t-search-box" onKeyPress={onKeyPress}/>
               <ClearButtonWrapper isVisible={!!searchText.length} inputHasFocus={hasFocus}>
                  <ControlButton themeName={theme} glyph={<Glyphs.Close16x16/>} buttonType={TscButtonType.tertiary} title={t('search.clear')} testId="clear-comment-filter-button" onClick={() => setSearchText('')}/>
               </ClearButtonWrapper>
               <ControlButton themeName={theme} glyph={<Glyphs.Search16x16/>} buttonType={TscButtonType.tertiary} title={t('search.search')} testId="t-search-icon" onClick={onSearchButtonClicked}/>
            </SearchForm>
         </SearchBarContainer>
      </CommentFilterWrapper>
   );
};

export interface ICommentFilterProps {
   searchText: string;
   setSearchText(text: string): void;
   executeSearch(): void;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {searchText, setSearchText, executeSearch}: ICommentFilterProps,
   {theme}: Partial<IStateMappedProps>
): ICommentFilterProps & IStateMappedProps => ({searchText, setSearchText, executeSearch, theme});

export default withMemoizedContexts(mapStatesToProps, themeStore)(CommentFilterBase);
